/**
 * License Insights in the portal (spec docs/superpowers/specs/2026-10-25-license-insights-design.md).
 *
 *   sanitizeLicenseInsights   the run callback keeps only the documented shape, bounded (never trusts the collector)
 *   validatePriceInput        one price the admin types: 0 to 10,000, at most two decimals
 *   buildLicenseView          what the Licenses page, the export and the report section render: money from the
 *                             customer's prices (or the catalog estimate), findings in order, the product table,
 *                             and the people behind the findings only when `showNames` (the customer's own admin,
 *                             read from the customer's bucket at request time: the run row never holds a name)
 *   licenseRecommendations    the cross-service recommendation shape (spec 8), the seam for a future Recommendations list
 *   licenseWatchSignals       the snapshot-diff Watch hooks of spec 9, phase 2 (the seam; Watch delivery is not wired)
 *   licenseReportSection      the "License summary" report rows (spec 10), never with names
 *   licenseKpiWords           the words of the two count KPIs, healthy case stated positively (Hugo Andrade, 2026-10-30)
 *   licenseUsageReport        the payload of the Licenses usage PDF: one row per license best practice, never with names
 *
 * Pure. Shared by the portal backend, the frontend and the tests.
 */
import { LICENSE_CATALOG_AS_OF, LIST_PRICE_CURRENCY, LIST_PRICE_LABEL, USAGE_SERVICE_LABELS, listPriceFor, productFor, productName, usageServicesFor } from './license-catalog.mjs';
import { LICENSE_BEST_PRACTICES, LICENSE_FINDING_IDS, licenseErrorWords, licenseGuidanceFor } from './license-guidance.mjs';
import { GRACE_OPTIONS, INACTIVITY_OPTIONS, PAIR_FINDINGS, VARIANT_KEYS, classifyPair, normalizeSettings, variantKey } from './license-rules.mjs';

export { LICENSE_FINDING_IDS };
export const MAX_PRICE = 10_000;
export const CURRENCIES = Object.freeze(['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'BRL', 'CHF', 'JPY', 'INR', 'MXN', 'SEK', 'NOK', 'DKK', 'NZD', 'ZAR']);
export const DEFAULT_CURRENCY = LIST_PRICE_CURRENCY;
/** Severity order shared with the security findings (critical first). */
export const SEVERITY_ORDER = Object.freeze(['critical', 'high', 'medium', 'low']);

// ---- Sanitizing the run callback -------------------------------------------------------------------------------

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const ID_RE = /^[A-Za-z0-9-]{1,64}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}(?:T[0-9:.]+Z?)?$/;
const MAX_COUNT = 10_000_000;
const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const int = (v, min = 0) => (Number.isInteger(v) && v >= min && v <= MAX_COUNT ? v : null);
const count = (v) => int(v) ?? 0;
const str = (v, max = 256) => (typeof v === 'string' && v.length > 0 ? v.slice(0, max) : null);
const date = (v) => (typeof v === 'string' && v.length <= 40 && DATE_RE.test(v) ? v : null);
const guid = (v) => (typeof v === 'string' && GUID_RE.test(v.toLowerCase()) ? v.toLowerCase() : null);
const id = (v) => (typeof v === 'string' && ID_RE.test(v) ? v : null);
const bool = (v) => v === true;
const optDays = (v) => (v === null ? null : int(v));
const list = (v, max, fn) => (Array.isArray(v) ? v.slice(0, max).map(fn).filter(Boolean) : []);
const USAGE = new Set(['ok', 'off', 'permission_missing', 'concealed', 'failed']);
const PRODUCT_STATUS = new Set(['active', 'grace', 'suspended', 'locked_out', 'deleted']);
const ALERT_KINDS = new Set(['trial_ending', 'expired_grace', 'suspended', 'locked_out']);
const SERVICES = ['exchange', 'onedrive', 'sharepoint', 'teams'];

function bySkuMap(v) {
  const out = {};
  if (!isObj(v)) return out;
  for (const [k, n] of Object.entries(v).slice(0, 300)) {
    const key = guid(k);
    const c = int(n);
    if (key && c !== null) out[key] = c;
  }
  return out;
}

/** UTF-8 bytes the stored insights may take in the run row (a DynamoDB item holds 400 KB with the security posture). */
export const MAX_INSIGHTS_BYTES = 120_000;

/** UTF-8 length of a value's JSON (what DynamoDB counts), in the portal backend and in the browser alike. */
export function utf8Bytes(value) {
  const text = JSON.stringify(value);
  return typeof Buffer !== 'undefined' ? Buffer.byteLength(text, 'utf8') : new TextEncoder().encode(text).length;
}

/**
 * The run row copy of a run's licenseInsights (security review of PR 422, H1 and M1): counts, products, per-finding
 * aggregates and opaque ids only. Never a person, a name, a sign-in name or a group name: those stay in the customer's
 * bucket and are read for the customer's own admin at request time (sanitizeLicenseDetail). Bounded in UTF-8 bytes:
 * above MAX_INSIGHTS_BYTES the group errors, then the alerts and unassigned rows, then the products are shortened; if
 * it still does not fit, null (the caller logs it), so the run row is always written.
 *
 * @param {unknown} value
 * @param {{ onOversize?: (bytes: number) => void }} [opts]
 */
export function sanitizeLicenseInsights(value, { onOversize } = {}) {
  const out = sanitizeShape(value);
  if (!out) return null;
  const shorten = (key) => {
    while (out[key].length > 0 && utf8Bytes(out) > MAX_INSIGHTS_BYTES) out[key] = out[key].slice(0, Math.floor(out[key].length / 2));
  };
  for (const key of ['groupErrors', 'subscriptionAlerts', 'unassigned', 'products']) shorten(key);
  const bytes = utf8Bytes(out);
  if (bytes > MAX_INSIGHTS_BYTES) {
    onOversize?.(bytes);
    return null;
  }
  return out;
}

function sanitizeShape(value) {
  if (!isObj(value) || value.version !== 1) return null;
  const ev = isObj(value.evaluated) ? value.evaluated : {};
  const counts = {};
  for (const v of VARIANT_KEYS) {
    const src = isObj(value.counts?.[v]) ? value.counts[v] : {};
    counts[v] = {};
    for (const f of [...PAIR_FINDINGS, 'grace']) {
      const c = isObj(src[f]) ? src[f] : {};
      counts[v][f] = { seats: count(c.seats), people: count(c.people), bySku: bySkuMap(c.bySku) };
    }
  }
  return {
    version: 1,
    generatedAt: date(value.generatedAt),
    evaluated: {
      skus: bool(ev.skus),
      subscriptions: bool(ev.subscriptions),
      assignments: bool(ev.assignments),
      assignmentStates: bool(ev.assignmentStates),
      signInActivity: bool(ev.signInActivity),
      usage: USAGE.has(ev.usage) ? ev.usage : 'off',
      productUsage: bool(ev.productUsage),
    },
    products: list(value.products, 200, (p) => {
      const skuId = guid(p?.skuId);
      if (!skuId) return null;
      return {
        skuId,
        skuPartNumber: str(p.skuPartNumber, 120),
        appliesTo: p.appliesTo === 'Company' ? 'Company' : 'User',
        status: PRODUCT_STATUS.has(p.status) ? p.status : 'active',
        free: bool(p.free),
        purchased: count(p.purchased),
        warning: count(p.warning),
        suspended: count(p.suspended),
        consumed: count(p.consumed),
        trial: bool(p.trial),
        trialEnds: date(p.trialEnds),
        renewal: date(p.renewal),
      };
    }),
    counts,
    unassigned: list(value.unassigned, 200, (u) => (guid(u?.skuId) ? { skuId: guid(u.skuId), seats: count(u.seats), renewal: date(u.renewal) } : null)),
    subscriptionAlerts: list(value.subscriptionAlerts, 100, (a) => (guid(a?.skuId) && ALERT_KINDS.has(a.kind)
      ? { skuId: guid(a.skuId), kind: a.kind, date: date(a.date), days: Number.isInteger(a.days) && Math.abs(a.days) <= 100_000 ? a.days : null, people: count(a.people), seats: count(a.seats) }
      : null)),
    // Opaque ids only: the group id, never its name, never a member.
    groupErrors: list(value.groupErrors, 50, (g) => (id(g?.groupId) && guid(g.skuId)
      ? { groupId: g.groupId, skuId: guid(g.skuId), error: str(g.error, 64) ?? 'Other', people: count(g.people) }
      : null)),
    serviceUnused: isObj(value.serviceUnused) ? Object.fromEntries(SERVICES.map((k) => [k, count(value.serviceUnused[k])])) : null,
    peopleTotal: count(value.peopleTotal),
  };
}

/**
 * The people behind the findings, from _license-insights/_latest.json in the customer's own bucket (read for the
 * customer's admin at request time, never stored by the portal): the people with their facts, the licensing group
 * names, and the group error samples. Bounded; null when it is not one.
 */
export function sanitizeLicenseDetail(value) {
  if (!isObj(value) || value.version !== 1) return null;
  const groupNames = Object.create(null);
  if (isObj(value.groupNames)) {
    for (const [k, n] of Object.entries(value.groupNames).slice(0, 200)) if (id(k)) groupNames[k] = str(n);
  }
  const groupErrors = Object.create(null);
  for (const g of list(value.groupErrors, 50, (x) => x)) {
    if (!id(g?.groupId) || !guid(g.skuId)) continue;
    groupErrors[`${g.groupId}|${guid(g.skuId)}|${str(g.error, 64) ?? 'Other'}`] = {
      groupName: str(g.groupName),
      sample: list(g.sample, 25, (x) => (id(x?.id) ? { id: x.id, name: str(x.name), upn: str(x.upn) } : null)),
    };
  }
  return {
    generatedAt: date(value.generatedAt),
    people: list(value.people, 300, (p) => {
      if (!id(p?.id)) return null;
      const out = {
        id: p.id, name: str(p.name), upn: str(p.upn), disabled: bool(p.disabled),
        disabledSince: date(p.disabledSince), disabledKnown: bool(p.disabledKnown), disabledDays: optDays(p.disabledDays ?? null),
        createdDays: optDays(p.createdDays ?? null),
        licenses: list(p.licenses, 50, (l) => {
          if (!guid(l?.sku)) return null;
          const out = { sku: guid(l.sku), group: id(l.group), redundantWith: guid(l.redundantWith) };
          // LIC-008: days since the person last used this product's services; absent when it was not checked.
          if (isObj(l) && 'useDays' in l) out.useDays = optDays(l.useDays);
          return out;
        }),
      };
      if ('signInDays' in p) out.signInDays = optDays(p.signInDays);
      if ('useDays' in p) out.useDays = optDays(p.useDays);
      return out;
    }),
    peopleTotal: count(value.peopleTotal),
    groupNames,
    groupErrors,
  };
}

const own = (map, key) => (map && Object.hasOwn(map, key) ? map[key] : undefined);

// ---- Prices ----------------------------------------------------------------------------------------------------

/**
 * Validates one price the admin entered: a number from 0 to 10,000 with at most two decimals ("per user per month";
 * an annual commitment is entered as its monthly equivalent). Returns { ok, value } or { ok: false, error }.
 */
export function validatePriceInput(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return { ok: false, error: 'Enter the price as a number, for example 36 or 31.50.' };
  if (value < 0 || value > MAX_PRICE) return { ok: false, error: `Enter a price from 0 to ${MAX_PRICE.toLocaleString('en-US')}.` };
  if (Math.abs(value * 100 - Math.round(value * 100)) > 1e-6) return { ok: false, error: 'Use at most two decimals, for example 31.50.' };
  return { ok: true, value: Math.round(value * 100) / 100 };
}

/** The price of one product: the customer's, else the catalog estimate (USD only), else none. */
export function priceFor(product, { prices = {}, currency = DEFAULT_CURRENCY } = {}) {
  const entered = prices?.[product.skuId];
  if (entered && typeof entered.amount === 'number') return { amount: entered.amount, source: 'entered', updatedAt: entered.updatedAt ?? null };
  if (product.free) return { amount: null, source: 'free' };
  const est = currency === LIST_PRICE_CURRENCY ? listPriceFor(product) : null;
  if (est) return { amount: est.amount, source: 'estimate', asOf: est.asOf, sourceUrl: est.source, label: LIST_PRICE_LABEL };
  return { amount: null, source: null };
}

// ---- The view --------------------------------------------------------------------------------------------------

const round2 = (n) => Math.round(n * 100) / 100;
const plural = (n, one, many = `${one}s`) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "Nov 3" or "Mar 1, 2027" (the year only when it is not the current one). */
export function shortDate(iso, now = Date.now()) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const d = new Date(t);
  const sameYear = d.getUTCFullYear() === new Date(now).getUTCFullYear();
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}${sameYear ? '' : `, ${d.getUTCFullYear()}`}`;
}

function money(seatsBySku, priceBySku) {
  let monthly = 0;
  let priced = 0;
  let unpriced = 0;
  for (const [sku, n] of Object.entries(seatsBySku)) {
    const p = priceBySku.get(sku);
    if (p && typeof p.amount === 'number') { monthly += n * p.amount; priced += n; } else unpriced += n;
  }
  if (priced === 0) return { monthly: null, yearly: null, partial: unpriced > 0 };
  return { monthly: round2(monthly), yearly: round2(monthly * 12), partial: unpriced > 0 };
}

function severityFor(id, impact, extra = {}) {
  if (id === 'LIC-006') return extra.error === 'CountViolation' ? 'high' : 'medium';
  if (id === 'LIC-005') {
    if (extra.kind === 'suspended' || extra.kind === 'locked_out') return 'high';
    return extra.days !== null && extra.days !== undefined && extra.days <= 7 ? 'high' : 'medium';
  }
  if (id === 'LIC-007') return 'low';
  return (impact.monthly ?? 0) >= 1000 || impact.seats >= 20 ? 'medium' : 'low';
}

const NOT_EVALUATED = Object.freeze({
  assignments: { reason: 'Needs the Users source: onGuard reads who holds which license from the users list.', unlock: 'data-collection' },
  assignmentStates: { reason: 'Microsoft did not return license assignment states for this app on the latest collection, so group licensing errors cannot be seen.', unlock: null },
  signInActivity: { reason: 'Needs Microsoft Entra ID P1 and the Audit and sign-ins permission for last sign-in dates.', unlock: 'data-collection' },
  subscriptions: { reason: 'Needs the Subscriptions and renewals source for trial and renewal dates.', unlock: 'data-collection' },
  usage_off: { reason: 'Turn on M365 usage activity to see who used no Microsoft 365 service.', unlock: 'data-collection' },
  usage_permission_missing: { reason: 'Needs the Microsoft 365 usage permission (Reports.Read.All) to read activity.', unlock: 'permissions' },
  usage_concealed: { reason: 'Your tenant hides user names in usage reports (Microsoft 365 admin center, Settings, Org settings, Reports), so onGuard cannot match activity to people. Showing names is your decision; onGuard never changes it.', unlock: 'concealed' },
  usage_failed: { reason: 'Microsoft did not return the usage report on the latest collection. onGuard reads it again with the next one.', unlock: null },
  productUsage_off: { reason: 'Turn on M365 usage activity in Data collection to see which licensed products people do not use.', unlock: 'data-collection' },
  productUsage_pending: { reason: 'Arrives with the next collection, after onGuard updates the collector in your AWS account.', unlock: null },
});

/** Why LIC-008 could not be evaluated, or null when it was. */
function productUsageNotEvaluated(ev) {
  if (!ev.assignments) return NOT_EVALUATED.assignments;
  if (ev.usage === 'off') return NOT_EVALUATED.productUsage_off;
  if (ev.usage !== 'ok') return NOT_EVALUATED[`usage_${ev.usage}`] ?? NOT_EVALUATED.productUsage_off;
  return ev.productUsage === true ? null : NOT_EVALUATED.productUsage_pending;
}

function personDetail(person, finding, license, ctx) {
  if (finding === 'LIC-001') {
    if (!person.disabledSince) return 'Disabled';
    return person.disabledKnown ? `Disabled since ${shortDate(person.disabledSince, ctx.now)}` : `Disabled before ${shortDate(person.disabledSince, ctx.now)}`;
  }
  if (finding === 'LIC-002') return person.signInDays === null ? 'Never signed in' : `No sign-in in ${person.signInDays} days`;
  if (finding === 'LIC-003') return `Included in ${productName({ skuId: license.redundantWith, skuPartNumber: ctx.partBySku.get(license.redundantWith) })}`;
  if (finding === 'LIC-007') return person.useDays === null ? 'No Microsoft 365 activity' : `No activity in ${person.useDays} days`;
  if (finding === 'LIC-008') return license.useDays === null || license.useDays === undefined ? 'Not used in the report period' : `Last used ${license.useDays} days ago`;
  return null;
}

/**
 * The Licenses page.
 *
 * @param {object} args
 * @param {object|null} args.insights - sanitized licenseInsights of the latest run that has them
 * @param {string|null} args.collectedAt - that run's timestamp
 * @param {object} [args.settings] - { inactivityDays, disabledGraceDays }
 * @param {object} [args.prices] - { [skuId]: { amount, updatedAt } } the customer entered
 * @param {string} [args.currency]
 * @param {object|null} [args.detail] - sanitizeLicenseDetail of the bucket copy: read only for the customer's own admin
 * @param {boolean} [args.showNames] - the customer's own admin: people and group names from `detail`; everyone else
 *   (and the admin when the bucket could not be read) gets counts
 * @param {number} [args.now]
 */
export function buildLicenseView({ insights, detail = null, collectedAt = null, settings = {}, prices = {}, currency = DEFAULT_CURRENCY, showNames: wantsNames = false, now = Date.now() }) {
  const showNames = wantsNames === true && detail !== null && typeof detail === 'object';
  const s = normalizeSettings(settings);
  const variant = variantKey(s.inactivityDays, s.disabledGraceDays);
  const ctr = insights.counts?.[variant] ?? {};
  const ev = insights.evaluated ?? {};
  const partBySku = new Map(insights.products.map((p) => [p.skuId, p.skuPartNumber]));
  const priceBySku = new Map(insights.products.map((p) => [p.skuId, priceFor(p, { prices, currency })]));
  const nameOf = (skuId) => productName({ skuId, skuPartNumber: partBySku.get(skuId) });
  const ctx = { now, partBySku };

  // People per finding, placed with the same rule the collector counted with (only for the customer's admin).
  const peopleBy = Object.fromEntries(PAIR_FINDINGS.map((f) => [f, []]));
  if (showNames) {
    for (const person of detail.people) {
      for (const l of person.licenses) {
        const f = classifyPair(person, l, s);
        if (!f || f === 'grace') continue;
        peopleBy[f].push({
          id: person.id, name: person.name, upn: person.upn, product: nameOf(l.sku), skuId: l.sku,
          detail: personDetail(person, f, l, ctx),
          group: l.group ? (own(detail.groupNames, l.group) ?? 'a group') : null,
        });
      }
    }
  }
  const peopleTruncated = showNames ? detail.peopleTotal > detail.people.length : false;

  const findings = [];
  const pairTitle = {
    'LIC-001': (n) => `${plural(n, 'license')} on disabled accounts`,
    'LIC-002': (n) => `${plural(n, 'license')} on accounts with no sign-in in ${s.inactivityDays} days`,
    'LIC-003': (n) => `${plural(n, 'license')} already included in another product the same people have`,
    'LIC-008': (n) => `${plural(n, 'license')} for products people did not use in ${s.inactivityDays} days`,
    'LIC-007': (n, people) => `${plural(people, 'licensed person', 'licensed people')} used no Microsoft 365 service in ${s.inactivityDays} days`,
  };
  for (const id of PAIR_FINDINGS) {
    const c = ctr[id] ?? { seats: 0, people: 0, bySku: {} };
    let notEvaluated = null;
    if (!ev.assignments) notEvaluated = NOT_EVALUATED.assignments;
    else if (id === 'LIC-002' && !ev.signInActivity) notEvaluated = NOT_EVALUATED.signInActivity;
    else if (id === 'LIC-007' && ev.usage !== 'ok') notEvaluated = NOT_EVALUATED[`usage_${ev.usage}`] ?? NOT_EVALUATED.usage_off;
    else if (id === 'LIC-008') notEvaluated = productUsageNotEvaluated(ev);
    // LIC-007 is a right-sizing review: seats and people, never money (spec 6).
    const m = id === 'LIC-007' ? { monthly: null, yearly: null, partial: false } : money(c.bySku, priceBySku);
    const impact = { monthly: m.monthly, yearly: m.yearly, partial: m.partial, seats: c.seats, people: c.people };
    impact.severity = severityFor(id, impact);
    findings.push({
      id, key: id, list: 'reclaim',
      title: notEvaluated ? { 'LIC-001': 'Licenses on disabled accounts', 'LIC-002': `Licenses on accounts with no sign-in in ${s.inactivityDays} days`, 'LIC-003': 'Licenses already included in another product', 'LIC-008': `Licenses for products people did not use in ${s.inactivityDays} days`, 'LIC-007': `Licensed people with no Microsoft 365 activity in ${s.inactivityDays} days` }[id] : pairTitle[id](c.seats, c.people),
      evaluated: notEvaluated ? { ...notEvaluated } : true,
      impact,
      products: Object.entries(c.bySku).map(([sku, n]) => ({ skuId: sku, name: nameOf(sku), seats: n, monthly: id === 'LIC-007' ? null : (priceBySku.get(sku)?.amount != null ? round2(n * priceBySku.get(sku).amount) : null) })).sort((a, b) => b.seats - a.seats),
      ...(showNames ? { people: peopleBy[id], peopleTruncated } : {}),
      ...(id === 'LIC-007' && insights.serviceUnused ? { services: insights.serviceUnused } : {}),
      ...(id === 'LIC-001' && (ctr.grace?.seats ?? 0) > 0 ? { inGrace: { seats: ctr.grace.seats, people: ctr.grace.people, days: s.disabledGraceDays } } : {}),
    });
  }

  // LIC-004: one finding, a row per product.
  {
    const bySku = Object.fromEntries(insights.unassigned.map((u) => [u.skuId, u.seats]));
    const seats = insights.unassigned.reduce((n, u) => n + u.seats, 0);
    const m = money(bySku, priceBySku);
    const impact = { monthly: m.monthly, yearly: m.yearly, partial: m.partial, seats, people: 0 };
    impact.severity = severityFor('LIC-004', impact);
    const next = insights.unassigned.map((u) => u.renewal).filter(Boolean).sort()[0] ?? null;
    findings.push({
      id: 'LIC-004', key: 'LIC-004', list: 'reclaim',
      title: insights.unassigned.length === 1 ? `${plural(seats, `${nameOf(insights.unassigned[0].skuId)} seat`)} bought and never assigned` : `${plural(seats, 'seat')} bought and never assigned`,
      nextStepDetail: next ? `Reduce the seat count before the renewal on ${shortDate(next, now)}, or assign them` : null,
      evaluated: ev.skus ? true : { reason: 'Needs the Products and seats source.', unlock: 'data-collection' },
      impact,
      products: insights.unassigned.map((u) => ({ skuId: u.skuId, name: nameOf(u.skuId), seats: u.seats, renewal: u.renewal, monthly: priceBySku.get(u.skuId)?.amount != null ? round2(u.seats * priceBySku.get(u.skuId).amount) : null })).sort((a, b) => b.seats - a.seats),
    });
  }

  // LIC-005: one row per alert; LIC-006: one row per group, product and error. Access and deadlines, not money.
  if (!ev.subscriptions) {
    findings.push({ id: 'LIC-005', key: 'LIC-005', list: 'attention', title: 'Trials and subscriptions ending', evaluated: { ...NOT_EVALUATED.subscriptions }, impact: { monthly: null, yearly: null, seats: 0, people: 0, severity: 'low' } });
  }
  for (const a of insights.subscriptionAlerts) {
    const name = nameOf(a.skuId);
    const when = a.date ? shortDate(a.date, now) : null;
    const title = {
      trial_ending: `${name} trial for ${plural(a.people, 'person', 'people')} ends ${when ?? 'soon'}`,
      expired_grace: `${name} expired and is in its grace period: ${plural(a.people, 'person', 'people')} still use it`,
      suspended: `${name} is suspended: ${plural(a.people, 'person', 'people')} lost its services`,
      locked_out: `${name} is locked out: ${plural(a.people, 'person', 'people')} lost its services`,
    }[a.kind];
    const impact = { monthly: null, yearly: null, seats: a.seats, people: a.people };
    impact.severity = severityFor('LIC-005', impact, a);
    findings.push({
      id: 'LIC-005', key: `LIC-005:${a.skuId}:${a.kind}`, list: 'attention', title, evaluated: true, impact, date: a.date,
      nextStepDetail: a.kind === 'trial_ending' && when ? `Decide before ${when}: buy, or let it end and tell the ${plural(a.people, 'person', 'people')}` : null,
    });
  }
  // Never "no errors" for a check that did not run: without the users list or its assignment states, not evaluated.
  if (!ev.assignments || !ev.assignmentStates) {
    findings.push({ id: 'LIC-006', key: 'LIC-006', list: 'attention', title: 'Group licensing errors', evaluated: { ...(ev.assignments ? NOT_EVALUATED.assignmentStates : NOT_EVALUATED.assignments) }, impact: { monthly: null, yearly: null, seats: 0, people: 0, severity: 'low' } });
  }
  for (const g of insights.groupErrors) {
    const named = showNames ? own(detail.groupErrors, `${g.groupId}|${g.skuId}|${g.error}`) : undefined;
    const group = showNames ? (named?.groupName ? `the group '${named.groupName}'` : 'a group') : 'a licensing group';
    const impact = { monthly: null, yearly: null, seats: g.people, people: g.people };
    impact.severity = severityFor('LIC-006', impact, g);
    findings.push({
      id: 'LIC-006', key: `LIC-006:${g.groupId}:${g.skuId}:${g.error}`, list: 'attention',
      title: `${plural(g.people, 'person', 'people')} did not get ${nameOf(g.skuId)} from ${group}: ${licenseErrorWords(g.error)}`,
      evaluated: true, impact,
      nextStepDetail: g.error === 'CountViolation' ? `Buy ${g.people} ${g.people === 1 ? 'seat' : 'seats'} or remove ${plural(g.people, 'person', 'people')} from the group` : null,
      ...(showNames ? { people: (named?.sample ?? []).map((p) => ({ id: p.id, name: p.name, upn: p.upn, product: nameOf(g.skuId), skuId: g.skuId, detail: licenseErrorWords(g.error), group: named?.groupName ?? null })), peopleTruncated: g.people > (named?.sample?.length ?? 0) } : {}),
    });
  }

  for (const f of findings) {
    const gd = licenseGuidanceFor(f.id);
    f.owner = gd.owner;
    f.nextStep = f.nextStepDetail ?? gd.nextStep;
    delete f.nextStepDetail;
    f.guidance = { impact: gd.impact, steps: gd.steps, links: gd.links };
    // The checklist (Hugo Andrade, 2026-10-30): a check with nothing to do reads as done, in its own words.
    // A Needs attention row (LIC-005, LIC-006) exists only when there is something to decide or fix: always 'act'.
    f.status = f.evaluated !== true ? 'not_evaluated' : (f.list === 'attention' || f.impact.seats > 0 || f.impact.people > 0 ? 'act' : 'ok');
    f.okTitle = okTitleFor(f.id, s.inactivityDays);
  }

  // The reclaim checklist: what to act on first (by monthly money, then seats), then the checks that are OK, then the
  // ones onGuard could not evaluate with what unlocks them (spec 5.1 and 5.2, revised 2026-10-30).
  const reclaim = findings.filter((f) => f.list === 'reclaim').sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status]
    || (b.impact.monthly ?? -1) - (a.impact.monthly ?? -1) || b.impact.seats - a.impact.seats);
  // Needs attention: by severity, then by date.
  const attention = findings.filter((f) => f.list === 'attention').sort((a, b) => SEVERITY_ORDER.indexOf(a.impact.severity) - SEVERITY_ORDER.indexOf(b.impact.severity)
    || String(a.date ?? '9999').localeCompare(String(b.date ?? '9999')));

  // Products: purchased, assigned, in use, wasted, unassigned, price, waste per month.
  // LIC-008 counts as waste only once evaluated (a collector that predates it sends zeros, never a guess).
  const counted = (id) => id !== 'LIC-008' || productUsageNotEvaluated(ev) === null;
  const wastedBySku = {};
  for (const f of WASTE_FINDINGS.filter(counted)) for (const [sku, n] of Object.entries(ctr[f]?.bySku ?? {})) wastedBySku[sku] = (wastedBySku[sku] ?? 0) + n;
  const unassignedBySku = Object.fromEntries(insights.unassigned.map((u) => [u.skuId, u.seats]));
  const products = insights.products.filter((p) => p.status !== 'deleted').map((p) => {
    const price = priceBySku.get(p.skuId);
    const wasted = wastedBySku[p.skuId] ?? 0;
    const unassigned = unassignedBySku[p.skuId] ?? 0;
    const known = productFor(p);
    return {
      skuId: p.skuId, skuPartNumber: p.skuPartNumber, name: nameOf(p.skuId), known: Boolean(known),
      free: p.free, appliesTo: p.appliesTo,
      purchased: p.purchased, assigned: p.consumed, inUse: Math.max(0, p.consumed - wasted), wasted, unassigned,
      price,
      wastePerMonth: !p.free && typeof price.amount === 'number' ? round2((wasted + unassigned) * price.amount) : null,
      status: productStatus(p, now),
    };
  }).sort((a, b) => (b.wastePerMonth ?? -1) - (a.wastePerMonth ?? -1) || (b.wasted + b.unassigned) - (a.wasted + a.unassigned) || a.name.localeCompare(b.name));

  const paidProducts = products.filter((p) => !p.free && p.appliesTo === 'User');
  const priced = paidProducts.filter((p) => typeof p.price.amount === 'number');
  const savings = [...WASTE_FINDINGS, 'LIC-004'].map((id) => findings.find((f) => f.key === id)).filter((f) => f && f.evaluated === true);
  const monthlyParts = savings.map((f) => f.impact.monthly).filter((v) => typeof v === 'number');
  const monthly = monthlyParts.length && priced.length ? round2(monthlyParts.reduce((a, b) => a + b, 0)) : (priced.length ? 0 : null);
  const wastedSeats = WASTE_FINDINGS.filter(counted).reduce((n, id) => n + (ctr[id]?.seats ?? 0), 0);
  const assigned = paidProducts.reduce((n, p) => n + p.assigned, 0);
  const purchased = paidProducts.reduce((n, p) => n + p.purchased, 0);
  const unassigned = insights.unassigned.reduce((n, u) => n + u.seats, 0);

  return {
    collectedAt,
    settings: s,
    options: { inactivityDays: [...INACTIVITY_OPTIONS], disabledGraceDays: [...GRACE_OPTIONS] },
    currency,
    evaluated: ev,
    showNames,
    kpis: {
      monthly,
      yearly: monthly === null ? null : round2(monthly * 12),
      wasted: { seats: wastedSeats, of: assigned },
      unassigned: { seats: unassigned, of: purchased },
    },
    prices: {
      paid: paidProducts.length,
      priced: priced.length,
      entered: priced.filter((p) => p.price.source === 'entered').length,
      estimated: priced.filter((p) => p.price.source === 'estimate').length,
      missing: paidProducts.length - priced.length,
      asOf: LICENSE_CATALOG_AS_OF,
      label: LIST_PRICE_LABEL,
    },
    reclaim,
    checks: checkSummary(reclaim),
    attention,
    products,
    usage: usageCoverage(insights, findings.find((f) => f.key === 'LIC-008'), nameOf),
    peopleTotal: showNames ? detail.peopleTotal : undefined,
  };
}

/** The findings whose seats are paid for and unused: the "wasted" KPI and the savings (LIC-007 is a review; LIC-004 is seats). */
const WASTE_FINDINGS = Object.freeze(['LIC-001', 'LIC-002', 'LIC-003', 'LIC-008']);
const STATUS_RANK = Object.freeze({ act: 0, ok: 1, not_evaluated: 2 });

function okTitleFor(id, days) {
  const bp = LICENSE_BEST_PRACTICES.find((b) => b.id === id);
  return bp ? bp.ok.replace('in the period', `in the last ${days} days`) : null;
}

/** "All 6 checks OK", or how many need action and how many could not be evaluated. */
function checkSummary(list) {
  const total = list.length;
  const act = list.filter((f) => f.status === 'act').length;
  const ok = list.filter((f) => f.status === 'ok').length;
  const notEvaluated = total - act - ok;
  const allOk = total > 0 && ok === total;
  let text;
  if (allOk) text = `All ${total} checks OK: nothing to reclaim.`;
  else if (act === 0) text = `${ok} of ${total} checks OK, nothing to reclaim in them; ${notEvaluated} not evaluated.`;
  else text = `${act} of ${total} checks need action${notEvaluated ? `; ${notEvaluated} not evaluated` : ''}.`;
  return { total, act, ok, notEvaluated, allOk, text };
}

/**
 * Which paid products onGuard can check for use (LIC-008) and which it cannot (Hugo Andrade, 2026-10-30: be explicit).
 * Products with at least one license assigned, by name. `unused` is the LIC-008 count when evaluated, else null.
 */
function usageCoverage(insights, lic008, nameOf) {
  const evaluated = lic008?.evaluated === true;
  const unusedBySku = evaluated ? Object.fromEntries((lic008.products ?? []).map((p) => [p.skuId, p.seats])) : {};
  const held = insights.products.filter((p) => !p.free && p.appliesTo === 'User' && p.status !== 'deleted' && p.consumed > 0);
  const checked = [];
  const notChecked = [];
  for (const p of held) {
    const services = usageServicesFor(p);
    if (services) checked.push({ skuId: p.skuId, name: nameOf(p.skuId), services: services.map((x) => USAGE_SERVICE_LABELS[x]), assigned: p.consumed, unused: evaluated ? unusedBySku[p.skuId] ?? 0 : null });
    else notChecked.push({ skuId: p.skuId, name: nameOf(p.skuId) });
  }
  const byName = (a, b) => a.name.localeCompare(b.name);
  return {
    evaluated,
    reason: evaluated ? null : lic008?.evaluated?.reason ?? null,
    unlock: evaluated ? null : lic008?.evaluated?.unlock ?? null,
    checked: checked.sort(byName),
    notChecked: notChecked.sort(byName),
  };
}

function productStatus(p, now) {
  if (p.trial) {
    const when = p.trialEnds ? shortDate(p.trialEnds, now) : null;
    return { word: when ? `Trial, ends ${when}` : 'Trial', tone: 'warn' };
  }
  return {
    active: { word: 'Active', tone: 'good' },
    grace: { word: 'Expired, in grace period', tone: 'warn' },
    suspended: { word: 'Suspended', tone: 'bad' },
    locked_out: { word: 'Locked out', tone: 'bad' },
  }[p.status] ?? { word: 'Active', tone: 'good' };
}

// ---- Seams for the other services (spec 8, 9, 10) -----------------------------------------------------------------

/**
 * The cross-service recommendation shape (spec 8): one per finding, ranked severity first, then monthly money, then
 * seats. firstSeen is unknown until a Recommendations store keeps history (the seam): null.
 */
export function licenseRecommendations(view) {
  return [...view.reclaim, ...view.attention]
    .filter((f) => f.evaluated === true && (f.impact.seats > 0 || f.impact.people > 0))
    .map((f) => ({
      id: f.id, key: f.key, service: 'licenses', title: f.title, owner: f.owner,
      impact: { monthly: f.impact.monthly, yearly: f.impact.yearly, seats: f.impact.seats, people: f.impact.people, severity: f.impact.severity },
      steps: [...f.guidance.steps], links: f.guidance.links.map((l) => ({ ...l })), evaluated: true,
      firstSeen: null, lastSeen: view.collectedAt, status: 'open',
    }))
    .sort((a, b) => SEVERITY_ORDER.indexOf(a.impact.severity) - SEVERITY_ORDER.indexOf(b.impact.severity)
      || (b.impact.monthly ?? -1) - (a.impact.monthly ?? -1) || b.impact.seats - a.impact.seats);
}

/**
 * Watch hooks from the daily snapshot (spec 9, phase 2): a new (group, product, error) in LIC-006, and a trial that
 * crossed 7 days. Pure diff of two sanitized insights; the Watch delivery (eventToggles, digest) is not wired yet.
 */
export function licenseWatchSignals(previous, current) {
  if (!current) return [];
  const before = new Set((previous?.groupErrors ?? []).map((g) => `${g.groupId}|${g.skuId}|${g.error}`));
  const out = [];
  for (const g of current.groupErrors) {
    if (before.has(`${g.groupId}|${g.skuId}|${g.error}`)) continue;
    out.push({ hook: 'license-group-error', urgency: g.error === 'CountViolation' ? 'immediate' : 'digest', groupId: g.groupId, skuId: g.skuId, error: g.error, people: g.people });
  }
  const wasNear = new Set((previous?.subscriptionAlerts ?? []).filter((a) => a.kind === 'trial_ending' && a.days !== null && a.days <= 7).map((a) => a.skuId));
  for (const a of current.subscriptionAlerts) {
    if (a.kind === 'trial_ending' && a.days !== null && a.days <= 7 && !wasNear.has(a.skuId)) out.push({ hook: 'license-trial-ending', urgency: 'digest', skuId: a.skuId, date: a.date, people: a.people });
  }
  return out;
}

/** The "License summary" report section (spec 10): KPIs, findings, products, price coverage. Never any name. */
export function licenseReportSection(view) {
  // A report is shared beyond the admin: it is built from the view without names (showNames false), never another.
  if (view.showNames) throw new Error('licenseReportSection: build it from a view without names');
  return {
    kpis: view.kpis,
    currency: view.currency,
    inactivityDays: view.settings.inactivityDays,
    priceCoverage: `Prices for ${view.prices.priced} of ${view.prices.paid} paid products (${view.prices.entered} entered, ${view.prices.estimated} Microsoft list price estimates).`,
    findings: [...view.reclaim, ...view.attention].map((f) => ({ id: f.id, title: f.title, owner: f.owner, nextStep: f.nextStep, seats: f.impact.seats, monthly: f.impact.monthly, evaluated: f.evaluated === true })),
    products: view.products.map((p) => ({ name: p.name, purchased: p.purchased, assigned: p.assigned, inUse: p.inUse, wasted: p.wasted, unassigned: p.unassigned, price: p.price.amount, priceSource: p.price.source, wastePerMonth: p.wastePerMonth })),
    disclaimer: LICENSES_DISCLAIMER,
  };
}

/** License and seat, in one line (Hugo Andrade, 2026-10-30). */
export const LICENSE_SEAT_HELP = 'A seat is one unit of a subscription you bought. A license is a seat assigned to a person.';

const count0 = (n) => (typeof n === 'number' && Number.isFinite(n) ? n : 0).toLocaleString('en-US');

/**
 * The words of the two count KPIs. The healthy case is stated positively ("65 of 65 in use", "None wasted"); the
 * problem case says how many and why. `percent` feeds the bar (the part in good use).
 */
export function licenseKpiWords(kpis) {
  const assigned = kpis?.wasted?.of ?? 0;
  const wasted = kpis?.wasted?.seats ?? 0;
  const purchased = kpis?.unassigned?.of ?? 0;
  const unassigned = kpis?.unassigned?.seats ?? 0;
  const inUse = Math.max(0, assigned - wasted);
  const seatsAssigned = Math.max(0, purchased - unassigned);
  const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : null);
  const licenses = assigned === 0
    ? { value: '0', unit: 'licenses assigned', caption: 'No paid licenses are assigned', healthy: true, percent: null }
    : { value: count0(inUse), unit: `of ${count0(assigned)} in use`, caption: wasted === 0 ? 'None wasted' : `${count0(wasted)} wasted: on disabled, inactive, unused or duplicate accounts`, healthy: wasted === 0, percent: pct(inUse, assigned) };
  const seats = purchased === 0
    ? { value: '0', unit: 'seats purchased', caption: 'No paid seats bought', healthy: true, percent: null }
    : { value: count0(seatsAssigned), unit: `of ${count0(purchased)} assigned`, caption: unassigned === 0 ? 'All seats assigned' : `${count0(unassigned)} bought and never assigned`, healthy: unassigned === 0, percent: pct(seatsAssigned, purchased) };
  return { licenses: { label: 'Licenses in use', ...licenses }, seats: { label: 'Seats assigned', ...seats } };
}

/** "$8,188" or "$39.00"; null when not a number. The report's money words (the page formats its own). */
export function moneyText(amount, currency = DEFAULT_CURRENCY, { cents = false } = {}) {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return null;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 }).format(amount);
  } catch {
    return `${amount.toFixed(cents ? 2 : 0)} ${currency}`;
  }
}

export const LICENSE_USAGE_REPORT_TYPE = 'licenses-usage';
export const LICENSE_USAGE_REPORT_SCHEMA = 1;
const PEOPLE_FINDINGS = new Set(['LIC-005', 'LIC-006', 'LIC-007']);
const cut = (t, max = 500) => {
  const text = String(t ?? '');
  return text.length > max ? `${text.slice(0, max - 3).trimEnd()}...` : text;
};

/**
 * The payload of the Licenses usage report (a PDF, report type 'licenses-usage'): how the tenant follows each license
 * best practice, the KPIs, which products onGuard can check for use, and the products. Built in the portal from a view
 * WITHOUT names and sent to the customer's report function, which validates its shape and renders it as is: every
 * value is a display string, never a person, a name, a sign-in name or a group name.
 */
export function licenseUsageReport(view) {
  if (view.showNames) throw new Error('licenseUsageReport: build it from a view without names');
  const c = view.currency;
  const kw = licenseKpiWords(view.kpis);
  const all = [...view.reclaim, ...view.attention];
  const practices = LICENSE_BEST_PRACTICES.map((bp) => {
    const rows = all.filter((f) => f.id === bp.id);
    const notEvaluated = rows.find((f) => f.status === 'not_evaluated');
    const acting = rows.filter((f) => f.status === 'act');
    const status = notEvaluated ? 'not_evaluated' : acting.length ? 'action' : 'followed';
    const people = PEOPLE_FINDINGS.has(bp.id);
    const n = acting.reduce((sum, f) => sum + (people ? f.impact.people : f.impact.seats), 0);
    const priced = acting.filter((f) => typeof f.impact.monthly === 'number');
    const monthly = priced.reduce((sum, f) => sum + f.impact.monthly, 0);
    let finding;
    if (status === 'not_evaluated') finding = notEvaluated.title;
    else if (status === 'followed') finding = okTitleFor(bp.id, view.settings.inactivityDays);
    else finding = acting.length === 1 ? acting[0].title : `${acting.length} items: ${acting.map((f) => f.title).join('; ')}`;
    const first = acting[0];
    return {
      id: bp.id,
      practice: bp.practice,
      status,
      finding: cut(finding),
      detail: cut(status === 'not_evaluated' ? notEvaluated.evaluated.reason : status === 'action' ? `${first.owner}: ${first.nextStep}.` : 'Nothing to do.'),
      impact: status === 'action' ? cut(`${plural(n, people ? 'person' : 'seat', people ? 'people' : 'seats')}${priced.length ? `, ${moneyText(monthly, c)} per month` : ''}`, 120) : '',
    };
  });
  const followed = practices.filter((p) => p.status === 'followed').length;
  const action = practices.filter((p) => p.status === 'action').length;
  const notEvaluated = practices.length - followed - action;
  let summary;
  if (followed === practices.length) summary = `All ${practices.length} license best practices are followed: nothing to reclaim.`;
  else if (action === 0) summary = `${followed} of ${practices.length} license best practices are followed and none needs action; ${notEvaluated} could not be evaluated.`;
  else summary = `${followed} of ${practices.length} license best practices are followed; ${action} need action${notEvaluated ? ` and ${notEvaluated} could not be evaluated` : ''}.`;
  const s = view.settings;
  const priceWords = (p) => {
    if (p.price.source === 'free') return 'Free';
    const m = moneyText(p.price.amount, c, { cents: true });
    if (!m) return 'No price';
    return p.price.source === 'estimate' ? `${m} (estimate)` : m;
  };
  return {
    schemaVersion: LICENSE_USAGE_REPORT_SCHEMA,
    asOf: view.collectedAt ?? null,
    settingsLine: `Inactive means no sign-in for ${s.inactivityDays} days. Disabled accounts count ${s.disabledGraceDays > 0 ? `after ${s.disabledGraceDays} days` : 'at once'}. Prices are per user per month in ${c}.`,
    summary,
    kpis: [
      { label: 'Potential savings per month', value: view.kpis.monthly === null ? 'Not priced' : moneyText(view.kpis.monthly, c), detail: view.kpis.monthly === null ? 'Add prices on the Licenses page to see savings' : `${moneyText(view.kpis.yearly, c)} a year` },
      { label: kw.licenses.label, value: `${kw.licenses.value} ${kw.licenses.unit}`, detail: kw.licenses.caption },
      { label: kw.seats.label, value: `${kw.seats.value} ${kw.seats.unit}`, detail: kw.seats.caption },
    ],
    help: LICENSE_SEAT_HELP,
    practices,
    usage: {
      intro: view.usage.evaluated
        ? `Microsoft reports per person use of Exchange, OneDrive, SharePoint and Teams. A product built on them is checked: a person uses it when any of its services shows activity in the last ${s.inactivityDays} days. Microsoft reports no per person use of the other products, so onGuard does not check them.`
        : `Not evaluated. ${view.usage.reason ?? 'Usage data is not available.'}`,
      checked: view.usage.checked.slice(0, 200).map((p) => ({ product: cut(p.name, 200), services: p.services.join(', '), assigned: count0(p.assigned), unused: p.unused === null ? 'Not evaluated' : count0(p.unused) })),
      notChecked: view.usage.notChecked.slice(0, 200).map((p) => cut(p.name, 200)),
    },
    products: view.products.slice(0, 200).map((p) => ({
      product: cut(p.name, 200), purchased: count0(p.purchased), assigned: count0(p.assigned), inUse: count0(p.inUse),
      wasted: count0(p.wasted), unassigned: count0(p.unassigned), price: priceWords(p), wastePerMonth: moneyText(p.wastePerMonth, c) ?? '',
    })),
    priceCoverage: `Prices for ${view.prices.priced} of ${view.prices.paid} paid products (${view.prices.entered} entered, ${view.prices.estimated} Microsoft list price estimates).`,
    disclaimer: LICENSES_DISCLAIMER,
  };
}

export const LICENSES_DISCLAIMER = 'onGuard reads your licenses and never changes them. Savings are estimates from the prices shown; your Microsoft agreement and renewal dates decide what you can reduce and when.';

/** CSV of the people behind the findings (the customer's admin only; the route audits every export). */
export function licensePeopleCsv(view) {
  const esc = (v) => {
    const t = v === null || v === undefined ? '' : String(v);
    // A leading = + - @ would run as a formula in a spreadsheet: prefix it.
    const safe = /^[=+\-@\t\r]/.test(t) ? `'${t}` : t;
    return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const rows = [['Finding', 'Name', 'Sign-in name', 'Product', 'Part number', 'Detail', 'Assigned by group']];
  for (const f of [...view.reclaim, ...view.attention]) {
    for (const p of f.people ?? []) rows.push([f.id, p.name, p.upn, p.product, view.products.find((x) => x.skuId === p.skuId)?.skuPartNumber ?? '', p.detail, p.group ?? '']);
  }
  return rows.map((r) => r.map(esc).join(',')).join('\r\n');
}
